import React from 'react';
import { string, shape, arrayOf } from 'prop-types';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import imageProps from 'TMPropTypes/imageProps';
import NavLink from 'TMComponents/NavLink';
import Arrow from 'TMComponents/Icons/Arrow';
import Facebook from 'TMComponents/Icons/Facebook';
import Instagram from 'TMComponents/Icons/Instagram';
import { spacing } from 'TMDesignSystem/spacing';
import borderRadius from 'TMDesignSystem/borders';
import { media } from 'TMDesignSystem/responsive';
import { Bold, RegText } from 'TMComponents/Typography/styles';

// Default disclaimer text - can be overridden via disclaimerText prop from CMS
const SHARE_DISCLAIMER =
    'The information in this blog was accurate when published. Please check the date, as details may have changed over time.';

const Container = styled.div`
    background: ${({ noBorder, theme }) =>
        noBorder ? 'transparent' : theme.colorSystem.UIElements[5]};
    border: ${({ noBorder, theme }) =>
        noBorder
            ? 'none'
            : `2px solid ${theme.colorSystem.UIElements[9]}`};
    padding: ${({ noBorder }) => (noBorder ? '0' : spacing.mediumLarge)};
    border-radius: ${({ noBorder }) =>
        noBorder ? '0' : borderRadius.small};
    margin-bottom: ${({ noBorder }) =>
        noBorder ? '0' : spacing.mediumLarge};
`;

const AVATAR_SIZE = 64;
const ICON_SIZE = 56;
const SOCIAL_ICON_SIZE = 28;

const TopSection = styled.div`
    display: flex;
    align-items: ${({ variant }) =>
        variant === 'bottom' ? 'center' : 'flex-start'};
    gap: ${spacing.medium};
    margin-bottom: ${({ hasMetaOrTags }) =>
        hasMetaOrTags ? spacing.mediumLarge : '0'};
`;

const ImageWrapper = styled.div`
    width: ${AVATAR_SIZE}px;
    height: ${AVATAR_SIZE}px;
    border-radius: ${borderRadius.full};
    overflow: hidden;
    flex-shrink: 0;

    .gatsby-image-wrapper {
        width: 100%;
        height: 100%;
    }
`;

const Content = styled.div`
    flex: 1;
`;

const AuthorName = styled.div`
    font-size: 20px;
    font-weight: 400;
    color: ${({ theme }) => theme.colorSystem.primary[1]};
    margin-bottom: ${spacing.small};

    span {
        color: ${({ theme }) => theme.colorSystem.primary[4]};
        font-weight: 700;
    }

    a {
        color: ${({ theme }) => theme.colorSystem.primary[4]};
        text-decoration: none;
        font-weight: 700;

        &:hover {
            text-decoration: underline;
        }
    }
`;

const JobTitle = styled.div`
    font-size: 20px;
    font-weight: 400;
    color: ${({ theme }) => theme.colorSystem.primary[1]};
`;

const MetaInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing.medium};
    margin-bottom: ${spacing.mediumLarge};
`;

const MetaItem = styled.div`
    display: flex;
    align-items: center;
    gap: ${spacing.small};
    font-size: 20px;
    font-weight: 400;
    color: ${({ theme }) => theme.colorSystem.primary[1]};

    svg {
        width: ${spacing.large};
        height: ${spacing.large};
        flex-shrink: 0;
        fill: ${({ theme }) => theme.colorSystem.primary[1]};
    }
`;

const Tags = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${spacing.small};
`;

const Tag = styled.span`
    padding: ${spacing.small} ${spacing.mediumLarge};
    background: ${({ theme }) => theme.colorSystem.primary[4]};
    border-radius: ${borderRadius.large};
    font-size: 16px;
    font-weight: 700;
    color: ${({ theme }) => theme.colorSystem.UIText[3]};
`;

const Bio = styled.p`
    font-size: 16px;
    font-weight: 400;
    line-height: 1.6;
    color: ${({ theme }) => theme.colorSystem.UIText[0]};
    margin: ${spacing.medium} 0 0 0;
`;

const ShareSection = styled.div`
    margin-top: ${spacing.mediumLarge};
`;

const ShareTitle = styled.h3`
    font-size: 24px;
    font-weight: 700;
    color: ${({ theme }) => theme.colorSystem.primary[1]};
    margin: 0 0 ${spacing.mediumLarge} 0;
`;

const SocialIcons = styled.div`
    display: flex;
    gap: ${spacing.medium};
    margin-bottom: ${spacing.mediumLarge};
`;

const SocialLink = styled.a`
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${ICON_SIZE}px;
    height: ${ICON_SIZE}px;
    background: ${({ theme }) => theme.colorSystem.primary[1]};
    border-radius: ${borderRadius.small};
    transition: transform 0.2s;
    color: ${({ theme }) => theme.colorSystem.UIText[3]};

    &:hover {
        transform: scale(1.1);
    }

    svg {
        width: ${SOCIAL_ICON_SIZE}px;
        height: ${SOCIAL_ICON_SIZE}px;

        path {
            fill: currentColor;
        }

        stop {
            stop-color: currentColor;
        }
    }
`;

const Disclaimer = styled.p`
    font-size: 16px;
    font-weight: 400;
    line-height: 1.6;
    color: ${({ theme }) => theme.colorSystem.UIText[0]};
    margin: 0;
`;

// Author profile card styles
const AuthorCard = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${spacing.medium};
    background-color: ${({ theme }) => theme.colorSystem.UIElements[5]};
    border: 2px solid ${({ theme }) => theme.colorSystem.UIElements[6]};
    border-radius: ${borderRadius.small};
    padding: ${spacing.mediumLarge};
    margin-bottom: ${spacing.xxLarge};
`;

const AuthorPhoto = styled.div`
    width: ${spacing.xxLarge};
    height: ${spacing.xxLarge};
    border-radius: ${borderRadius.full};
    overflow: hidden;
    flex-shrink: 0;

    ${media.tablet} {
        width: ${spacing.huge};
        height: ${spacing.huge};
    }

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const AuthorContent = styled.div`
    flex: 1;
`;

const AuthorProfileName = styled.h1`
    ${Bold};
    font-size: 1.75rem;
    color: ${({ theme }) => theme.colorSystem.primary[1]};
    margin: 0 0 ${spacing.tiny} 0;

    ${media.tablet} {
        font-size: 2rem;
    }

    span {
        color: ${({ theme }) => theme.colorSystem.secondary[3]};
    }
`;

const AuthorJobTitle = styled.p`
    ${Bold};
    ${RegText};
    color: ${({ theme }) => theme.colorSystem.primary[1]};
    margin: 0 0 ${spacing.medium} 0;
    font-weight: 700 !important;
`;

const AuthorBioText = styled.div`
    ${RegText};
    color: ${({ theme }) => theme.colorSystem.primary[1]};
    margin: 0 0 ${spacing.medium} 0;

    p {
        margin: 0 0 ${spacing.medium} 0;

        &:last-child {
            margin-bottom: 0;
        }
    }
`;

const ReadMore = styled(NavLink)`
    ${Bold};
    ${RegText};
    color: ${({ theme }) => theme.colorSystem.primary[1]};
    display: inline-flex;
    align-items: center;
    gap: ${spacing.small};
    text-decoration: none;
    font-weight: 700 !important;

    svg {
        width: ${spacing.mediumLarge};
        height: ${spacing.large};
    }
`;

const renderBottomVariant = ({
    bio,
    shareText,
    shareUrl,
    disclaimerText
}) => (
    <>
        {bio && <Bio>{bio}</Bio>}
        <ShareSection>
            <ShareTitle>{shareText || 'Share this blog on:'}</ShareTitle>
            <SocialIcons>
                <SocialLink
                    href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Share on Facebook">
                    <Facebook />
                </SocialLink>
                <SocialLink
                    href="https://www.instagram.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Share on Instagram">
                    <Instagram />
                </SocialLink>
            </SocialIcons>
            <Disclaimer>{disclaimerText || SHARE_DISCLAIMER}</Disclaimer>
        </ShareSection>
    </>
);

const renderTopVariant = ({ publishDate, readTime, tags }) => (
    <>
        <MetaInfo>
            {publishDate && (
                <MetaItem>
                    <svg viewBox="0 0 24 24">
                        <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2z" />
                    </svg>
                    {publishDate}
                </MetaItem>
            )}
            {readTime && (
                <MetaItem>
                    <svg viewBox="0 0 24 24">
                        <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.2 3.2.8-1.3-4.5-2.7V7z" />
                    </svg>
                    {readTime}
                </MetaItem>
            )}
        </MetaInfo>
        {tags && tags.length > 0 && (
            <Tags>
                {tags.map((tag) => (
                    <Tag key={tag}>{tag}</Tag>
                ))}
            </Tags>
        )}
    </>
);

// Main AuthorBio component
const AuthorBio = ({ author, authorName, ...props }) => {
    // If no author object and no authorName, return null
    if (!author && !authorName) {
        return null;
    }

    // If author object is passed, render author profile card
    if (author) {
        return (
            <AuthorCard>
                <AuthorPhoto>
                    <img
                        src={getImage(author.photo)?.images?.fallback?.src}
                        alt={author.name}
                    />
                </AuthorPhoto>
                <AuthorContent>
                    <AuthorProfileName>
                        {author.name}
                        <span>.</span>
                    </AuthorProfileName>
                    <AuthorJobTitle>{author.jobTitle}</AuthorJobTitle>
                    <AuthorBioText
                        dangerouslySetInnerHTML={{
                            __html: (
                                author.longBio ||
                                `${author.jobTitle} with expertise in financial planning and budgeting.`
                            )
                                .replace(/\n\n/g, '</p><p>')
                                .replace(/^(.*)$/, '<p>$1</p>')
                        }}
                    />
                    <ReadMore href="#articles">
                        Read articles by {author.name}
                        <Arrow color="currentColor" />
                    </ReadMore>
                </AuthorContent>
            </AuthorCard>
        );
    }

    // Otherwise render blog article author bio
    const {
        authorSlug,
        jobTitle,
        authorImage,
        publishDate,
        readTime,
        tags,
        variant,
        bio,
        shareUrl,
        shareText,
        disclaimerText,
        noBorder
    } = props;

    return (
        <Container noBorder={noBorder}>
            <TopSection
                variant={variant}
                hasMetaOrTags={
                    variant === 'top' &&
                    (publishDate || readTime || (tags && tags.length > 0))
                }>
                {authorImage && (
                    <ImageWrapper>
                        <GatsbyImage
                            image={authorImage}
                            alt={authorName || 'Author'}
                        />
                    </ImageWrapper>
                )}
                <Content>
                    <AuthorName>
                        Written by{' '}
                        {authorSlug ? (
                            <NavLink
                                href={`/blog/meet-the-experts/${authorSlug}/`}>
                                {authorName}
                            </NavLink>
                        ) : (
                            <span>{authorName}</span>
                        )}
                    </AuthorName>
                    {jobTitle && <JobTitle>{jobTitle}</JobTitle>}
                </Content>
            </TopSection>
            {variant === 'bottom' &&
                renderBottomVariant({
                    bio,
                    shareText,
                    shareUrl,
                    disclaimerText
                })}
            {variant === 'top' &&
                renderTopVariant({ publishDate, readTime, tags })}
        </Container>
    );
};

AuthorBio.propTypes = {
    author: shape({
        name: string.isRequired,
        jobTitle: string.isRequired,
        longBio: string,
        photo: shape({}).isRequired
    }),
    authorName: string,
    authorSlug: string,
    jobTitle: string,
    authorImage: shape(imageProps),
    publishDate: string,
    readTime: string,
    tags: arrayOf(string),
    variant: string,
    bio: string,
    shareUrl: string,
    shareText: string,
    disclaimerText: string,
    noBorder: string
};

AuthorBio.defaultProps = {
    author: null,
    authorSlug: '',
    jobTitle: '',
    authorImage: null,
    publishDate: '',
    readTime: '',
    tags: [],
    variant: 'top',
    bio: '',
    shareUrl: '',
    shareText: '',
    disclaimerText: '',
    noBorder: false
};

export default AuthorBio;
